import {Checkbox, NewButton} from 'components/ui';
import {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import InputMask from 'react-input-mask';
import {connect} from 'react-redux';
import {ApplicationState} from 'store/rootReducer';
import isEmpty from 'utils/functions/isEmpty';
import {formatDate} from 'utils/functions/validation/date';
import {StoreStatus} from 'utils/types/store';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {Input} from 'letrus-ui';
import ModalWindow from '../../components/ModalWindow';
import {
  fetchProfileRequest,
  getUpdateProfileRequestStatus,
  updateProfileRequest,
} from '../../store/reducers/profile';
import styles from './Profile.module.scss';

export interface FormValues {
  email: string;
  phone_number: string;
  is_subscribed_to_email?: boolean;
}

interface DispatchProps {
  updateProfileRequest: typeof updateProfileRequest;
  fetchProfileRequest: typeof fetchProfileRequest;
}

interface StateProps {
  updateProfileRequestStatus: StoreStatus;
}

export type ProfileProps = DispatchProps & StateProps;

function Profile({
  updateProfileRequest,
  updateProfileRequestStatus,
  fetchProfileRequest,
}: ProfileProps): JSX.Element {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [updateResponse, setUpdateResponse] = useState('');

  const {user, userProfile} = getUserData();

  const {
    register,
    handleSubmit,
    control,
    formState: {errors: formErrors, isSubmitted},
    watch: formWatch,
  } = useForm<FormValues>({
    defaultValues: {
      email: userProfile?.personal_email ?? user?.email,
      phone_number: userProfile?.phone_number,
      is_subscribed_to_email: userProfile?.is_subscribed_to_email,
    },
    mode: 'all',
  });

  useEffect(() => {
    fetchProfileRequest();
  }, []);

  useEffect(() => {
    if (updateProfileRequestStatus.fulfilled && isSubmitted) {
      setIsOpenModal(true);

      setUpdateResponse('Perfil atualizado com sucesso!');
    } else if (updateProfileRequestStatus.error) {
      setIsOpenModal(true);

      setUpdateResponse('Ocorreu um erro! Por favor, tente novamente.');
    }
  }, [updateProfileRequestStatus]);

  function onSubmit(data: FormValues) {
    updateProfileRequest({
      id: Number(userProfile?.id),
      phone_number: data?.phone_number?.replace(/[^\d.]/g, '') ?? '',
      personal_email: data?.email ?? '',
      is_subscribed_to_email: data?.is_subscribed_to_email ?? false,
    });
  }

  const userBirth = userProfile?.birth && formatDate(userProfile?.birth);

  const {email, phone_number} = formWatch();

  const isSubmitButtonEnabled =
    !!email && !!phone_number && isEmpty(formErrors);

  const emailPatternRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  function onCloseModal() {
    setIsOpenModal(false);
    setIsEditMode(false);
  }

  return (
    <main className={styles.container}>
      <header>
        <h1>Meu Perfil</h1>
        {!isEditMode && (
          <NewButton
            userRole="student"
            kind="primary"
            onClick={() => setIsEditMode(!isEditMode)}
            icon="pencil-alt"
          >
            Editar informações
          </NewButton>
        )}
      </header>

      <div className={styles.content}>
        <section>
          <form onSubmit={handleSubmit(onSubmit)}>
            <h2 className={styles.firstTitle}>Nome</h2>
            <p className={isEditMode ? styles.fakeInput : ''}>
              {user?.first_name}
            </p>

            <h2>Sobrenome</h2>
            <p className={isEditMode ? styles.fakeInput : ''}>
              {user?.last_name}
            </p>

            <h2>Email</h2>
            {isEditMode ? (
              <Input
                data-testid="email-input"
                id="email"
                label="email"
                className={styles.inputs}
                errorMessage={
                  formErrors?.email &&
                  'Email inválido. Por favor, verifique e tente novamente.'
                }
                {...register('email', {
                  required: true,
                  pattern: {
                    value: emailPatternRegex,
                    message: 'Insira um endereço de e-mail válido',
                  },
                })}
              />
            ) : (
              <p data-testid="email-field">
                {userProfile?.personal_email ?? user?.email}
              </p>
            )}

            <h2>Telefone</h2>
            {isEditMode ? (
              <Controller
                name="phone_number"
                control={control}
                render={({field}) => (
                  <InputMask
                    data-testid="phone-input"
                    mask="(99) 999999999"
                    id="phone_number"
                    aria-label="phone_number"
                    maskplaceholder="(99) 999999999"
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    className={styles.inputs}
                    errorMessage={
                      formErrors?.phone_number &&
                      'Telefone inválido. Por favor, verifique e tente novamente.'
                    }
                  />
                )}
              />
            ) : (
              <p data-testid="phone-field">{userProfile?.phone_number ?? ''}</p>
            )}

            <h2>Data de nascimento</h2>
            <p className={isEditMode ? styles.fakeInput : ''}>{userBirth}</p>

            <Controller
              name="is_subscribed_to_email"
              control={control}
              render={({field}) => (
                <Checkbox
                  id="subscribed_email"
                  labelText="Desejo receber e-mails com lembretes de atividades"
                  name="subscribed_to_email"
                  onChange={field.onChange}
                  checked={
                    isEditMode
                      ? field.value
                      : userProfile?.is_subscribed_to_email
                  }
                  disabled={!isEditMode}
                />
              )}
            />

            {isEditMode && (
              <div className={styles.buttons}>
                <NewButton
                  kind="primary"
                  onClick={() => setIsEditMode(!isEditMode)}
                  icon="close"
                >
                  Cancelar
                </NewButton>
                <NewButton
                  userRole="student"
                  type="submit"
                  isLoading={updateProfileRequestStatus.loading}
                  disabled={!isSubmitButtonEnabled}
                  icon="check"
                >
                  Salvar
                </NewButton>
              </div>
            )}
          </form>
        </section>
      </div>

      <ModalWindow
        show={isOpenModal}
        onHide={onCloseModal}
        loading={updateProfileRequestStatus.loading}
        message={updateResponse}
        closeButton={onCloseModal}
      />
    </main>
  );
}

export default connect(
  (state: ApplicationState) => ({
    updateProfileRequestStatus: getUpdateProfileRequestStatus(state),
  }),
  {
    updateProfileRequest,
    fetchProfileRequest,
  },
)(Profile);
